// src/hooks/plan/useReferencePage.js
import { useState, useEffect, useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import planApi from '@api/planApi';
import { AuthContext } from '@context';

export default function useReferencePage() {
  const queryClient = useQueryClient();
  const { auth } = useContext(AuthContext);
  const isAdmin = auth?.role === 'ROLE_ADMIN' || auth?.roleId === 'R0';

  const [selectedCatId, setSelectedCatId] = useState(null);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 10;

  // 분류 목록
  const categoriesQuery = useQuery({
    queryKey: ['plan', 'refCategories'],
    queryFn: () => planApi.getRefCategories(),
    select: (data) => data?.list || [],
  });

  const categories = categoriesQuery.data || [];

  // refType 변경 시 첫 번째 분류 자동 선택
  useEffect(() => {
    if (categories.length > 0 && (selectedCatId === null || !categories.find(c => c.catId === selectedCatId))) {
      setSelectedCatId(categories[0].catId);
      setPage(0);
    }
  }, [categories]); // eslint-disable-line react-hooks/exhaustive-deps

  // 게시글 목록
  const postsQuery = useQuery({
    queryKey: ['plan', 'refPosts', selectedCatId, page],
    queryFn: () => planApi.listRefPosts(selectedCatId, page, PAGE_SIZE),
    enabled: !!selectedCatId,
  });

  const posts = postsQuery.data?.list || [];
  const totalCount = postsQuery.data?.totalCount || 0;

  // 분류 추가
  const addCategoryMutation = useMutation({
    mutationFn: ({ catName }) => planApi.addRefCategory(catName),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['plan', 'refCategories'] }),
  });

  // 분류 삭제
  const deleteCategoryMutation = useMutation({
    mutationFn: (catId) => planApi.deleteRefCategory(catId),
    onSuccess: (_, catId) => {
      queryClient.invalidateQueries({ queryKey: ['plan', 'refCategories'] });
      if (selectedCatId === catId) setSelectedCatId(null);
    },
  });

  // 게시글 작성
  const createPostMutation = useMutation({
    mutationFn: (formData) => planApi.createRefPost(formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plan', 'refPosts', selectedCatId] });
      setPage(0);
    },
  });

  // 게시글 수정
  const updatePostMutation = useMutation({
    mutationFn: ({ refId, formData }) => planApi.updateRefPost(refId, formData),
    onSuccess: (_, { refId }) => {
      queryClient.invalidateQueries({ queryKey: ['plan', 'refPosts', selectedCatId] });
      queryClient.invalidateQueries({ queryKey: ['plan', 'refPost', refId] });
    },
  });

  // 게시글 삭제
  const deletePostMutation = useMutation({
    mutationFn: (refId) => planApi.deleteRefPost(refId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plan', 'refPosts', selectedCatId] });
      setPage(0);
    },
  });

  // 첨부파일 개별 삭제
  const deletePostFileMutation = useMutation({
    mutationFn: ({ refId, fileSn }) => planApi.deleteRefFile(refId, fileSn),
    onSuccess: (_, { refId }) =>
      queryClient.invalidateQueries({ queryKey: ['plan', 'refPost', refId] }),
  });

  return {
    isAdmin,
    selectedCatId, setSelectedCatId,
    page, setPage,
    PAGE_SIZE,
    categories,
    isCategoriesLoading: categoriesQuery.isLoading,
    posts,
    totalCount,
    isPostsLoading: postsQuery.isFetching,
    addCategory: addCategoryMutation.mutateAsync,
    deleteCategory: deleteCategoryMutation.mutateAsync,
    createPost: createPostMutation.mutateAsync,
    updatePost: updatePostMutation.mutateAsync,
    deletePost: deletePostMutation.mutateAsync,
    deletePostFile: deletePostFileMutation.mutateAsync,
  };
}
